import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MessageSquare, Loader2, Send, Video, Users, User } from "lucide-react";
import { initials, ageDisplay, fmtDateTime } from "@/lib/format";
import VideoCallDialog from "@/components/VideoCallDialog";
import { useAuth } from "@/lib/auth";

/** Shared clinical canvas for multi-doctor appointments.
 *  Invited doctors land here to read the host's consultation notes and add a
 *  second-opinion note that appends to the host's chart (and pings the host). */
export default function SecondOpinion() {
  const { appointmentId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null); // { consultation, appointment, patient, role_in_appt }
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [dx, setDx] = useState("");
  const [saving, setSaving] = useState(false);
  const [openCall, setOpenCall] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/${appointmentId}/consultation`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not authorized to view this appointment");
      nav("/appointments");
    } finally { setLoading(false); }
  }, [appointmentId, nav]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!note.trim()) { toast.error("Please write a note"); return; }
    if (!state?.consultation?.id) {
      toast.error("The host has not saved the consultation yet — please wait until they save the primary notes.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/consultations/${state.consultation.id}/second-opinion`, {
        note: note.trim(),
        diagnosis: dx.trim() || null,
      });
      toast.success("Second opinion recorded — the host has been notified");
      setNote(""); setDx("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading || !state) return <Layout><div className="py-20 text-center text-[#8A9992]"><Loader2 className="animate-spin inline mr-2" size={16} />Loading shared canvas…</div></Layout>;

  const { consultation: c, appointment: a, patient: p, role_in_appt: role } = state;
  const notes = (c?.second_opinions || []);

  return (
    <Layout>
      <div className="mb-5 md:mb-6 flex items-center justify-between gap-3 flex-wrap">
        <button className="inline-flex items-center gap-2 text-[#47554D] text-sm hover:text-[#0A4C36]" onClick={() => nav(-1)} data-testid="so-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-[11px] uppercase tracking-widest text-[#8A9992] inline-flex items-center gap-2">
          <Users size={12} className="text-[#0A4C36]" />
          Shared clinical canvas · <span className="text-[#0A4C36] font-semibold">{role === "host" ? "Host" : "Second opinion"}</span>
        </div>
      </div>

      {/* Patient identity */}
      {p && (
        <div className="card-soft p-5 mb-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#0A4C36]/10 text-[#0A4C36] flex items-center justify-center font-semibold text-sm flex-none">
            {initials(p.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[#1F2A24] truncate">{p.name}</div>
            <div className="text-xs text-[#8A9992] truncate">{p.gender} · {ageDisplay(p)} · Appt on {a.date} at {a.time}</div>
          </div>
          <button onClick={() => setOpenCall(true)} className="btn-outline-emerald flex-none" data-testid="so-video-btn">
            <Video size={14} /> Join call
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
        {/* Left: Host consultation summary (read-only) */}
        <section className="lg:col-span-7 card-soft p-5 md:p-6" data-testid="so-host-summary">
          <div className="field-label mb-3">Host&rsquo;s consultation</div>
          {!c ? (
            <div className="text-xs text-[#8A9992] py-6 text-center bg-[#F7F4EC]/60 rounded-xl border border-dashed border-[#E8E6E1]">
              The host doctor has not saved the consultation yet.<br />
              You can still join the video call and add notes once the host saves.
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              <ReadonlyBlock label="Chief complaint" value={c.chief_complaint} />
              <ReadonlyList label="History" values={c.history_list} fallback={c.history} />
              <ReadonlyBlock label="Diagnosis" value={c.diagnosis} highlight />
              <ReadonlyList label="Examination" values={c.examination_list} fallback={c.examination} />
              <ReadonlyList label="Advice" values={c.advice_list} fallback={c.advice} />
              {(c.medications || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Prescription</div>
                  <div className="space-y-1.5">
                    {c.medications.map((m, i) => (
                      <div key={i} className="text-[13px] text-[#1F2A24] leading-snug">
                        <b>{m.name}</b>{m.strength ? ` · ${m.strength}` : ""} {m.instructions ? <span className="text-[#5C7568]">— {m.instructions}</span> : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[10px] text-[#8A9992] uppercase tracking-widest pt-2 border-t border-[#EFECE5]">Saved {fmtDateTime(c.created_at)}</div>
            </div>
          )}
        </section>

        {/* Right: Add second-opinion note + notes list */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="card-soft p-5 md:p-6" data-testid="so-write-card">
            <div className="field-label mb-3 inline-flex items-center gap-2">
              <MessageSquare size={13} className="text-[#0A4C36]" /> Add your {role === "host" ? "note" : "second opinion"}
            </div>
            <div className="space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Diagnosis (optional)</div>
                <input value={dx} onChange={(e) => setDx(e.target.value)} className="input-clean" placeholder="e.g. Rule out cardiac ischemia" data-testid="so-diagnosis" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">Note</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={5}
                  className="input-clean resize-y leading-relaxed"
                  placeholder="Your clinical opinion, additional history, suggested workup, or a management plan…"
                  data-testid="so-note"
                />
              </div>
              <button
                onClick={submit}
                disabled={saving || !note.trim() || !c}
                className="btn-emerald w-full justify-center"
                data-testid="so-submit"
                title={!c ? "Waiting for host to save the primary consultation" : ""}
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {saving ? "Sending…" : (role === "host" ? "Add note" : "Send second opinion")}
              </button>
              {!c && (
                <div className="text-[11px] text-[#8A5A34] text-center" data-testid="so-waiting-hint">
                  Waiting for the host to save the primary consultation before you can send a second opinion.
                </div>
              )}
            </div>
          </div>

          <div className="card-soft p-5 md:p-6" data-testid="so-notes-list">
            <div className="field-label mb-3">
              Timeline {notes.length > 0 && <span className="text-[#8A9992] font-normal">· {notes.length}</span>}
            </div>
            {notes.length === 0 ? (
              <div className="text-xs text-[#8A9992] py-6 text-center bg-[#F7F4EC]/60 rounded-xl border border-dashed border-[#E8E6E1]">
                No second-opinion notes yet. Yours will land here.
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-2xl bg-[#F7F4EC]/70 border border-[#EFECE5] p-3.5" data-testid={`so-note-${n.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-full bg-[#0A4C36] text-white text-[10px] font-semibold flex items-center justify-center flex-none">
                        {initials(n.doctor_name || "")}
                      </div>
                      <div className="text-xs font-semibold text-[#1F2A24] truncate">
                        {n.doctor_name}
                        <span className="ml-1 text-[9px] font-bold uppercase tracking-widest text-[#0A4C36]">· {n.role}</span>
                      </div>
                      <div className="ml-auto text-[10px] text-[#8A9992]">{fmtDateTime(n.created_at)}</div>
                    </div>
                    {n.diagnosis && <div className="text-[13px] font-semibold text-[#0A4C36] mb-1">{n.diagnosis}</div>}
                    <div className="text-[13px] text-[#1F2A24] leading-relaxed whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {role === "host" && p && (
            <Link
              to={`/consultation/new?patient=${p.id}&appt=${appointmentId}`}
              className="btn-outline-emerald w-full justify-center"
              data-testid="so-open-full-consult"
            >
              <User size={14} /> Open full consultation form
            </Link>
          )}
        </aside>
      </div>

      <VideoCallDialog
        open={openCall}
        onOpenChange={setOpenCall}
        appointment={a || { id: appointmentId }}
        doctorName={user?.name}
      />
    </Layout>
  );
}

function ReadonlyBlock({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">{label}</div>
      <div className={`${highlight ? "text-[15px] font-semibold text-[#0A4C36]" : "text-[13px] text-[#1F2A24]"} whitespace-pre-wrap leading-relaxed`}>{value}</div>
    </div>
  );
}

function ReadonlyList({ label, values, fallback }) {
  const arr = Array.isArray(values) && values.length ? values : (fallback ? [fallback] : []);
  if (arr.length === 0) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#5C7568] mb-1">{label}</div>
      <ul className="list-disc list-inside space-y-0.5 text-[13px] text-[#1F2A24]">
        {arr.map((v, i) => <li key={i}>{v}</li>)}
      </ul>
    </div>
  );
}
